/* ==========================================================================
   Build Your Box  —  sections/c-build-your-box.liquid
   Bundle builder: pick N products from tabbed collections, configure each one
   in a right-hand mini-PDP drawer, then add the whole bundle to the cart in a
   single request and open the theme's cart drawer.
   Every label, colour and tier comes from the section settings (see the JSON
   config script printed by the section), nothing is hard-coded here.
   ========================================================================== */

(function () {
  'use strict';

  var MONEY_FALLBACK = '${{amount}}';

  function formatMoney(cents) {
    var format =
      (window.theme && window.theme.money_format) ||
      (window.Shopify && window.Shopify.money_format) ||
      MONEY_FALLBACK;

    if (window.theme && window.theme.Shopify && window.theme.Shopify.formatMoney) {
      return window.theme.Shopify.formatMoney(Math.round(cents), format);
    }

    // Minimal stand-in used only if theme.js has not booted yet.
    var parts = (Math.round(cents) / 100).toFixed(2).split('.');
    var value = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* Labels use [token] placeholders so merchants can rewrite them freely. */
  function fillTokens(template, tokens) {
    if (!template) return '';
    return String(template).replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(tokens, key) ? tokens[key] : match;
    });
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).
      replace(/&/g, '&amp;').
      replace(/</g, '&lt;').
      replace(/>/g, '&gt;').
      replace(/"/g, '&quot;');
  }

  function parseJSONScript(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  /* Cart apps replace the theme's drawer wholesale. Detect the ones this shop
     could plausibly run rather than guess from the absence of theme markup. */
  function hasCartApp() {
    if (window.UpCart || window.upcart || window.Rebuy) return true;
    return !!document.querySelector(
      '#upcart-cart, .upcart-cart, [id^="upcart"], [class*="upcart"], #rebuy-cart, .slidecart'
    );
  }

  /* --------------------------------------------------------- persistence --
     The bundle survives "Add to cart": the cart line and the builder are two
     views of the same thing, and the cart's Edit link brings the shopper back
     here to change it. Emptying the builder on add would have made that link
     land on a blank box. The state is dropped only when the bundle leaves the
     cart — Remove bundle, or a completed checkout, both of which are checked
     against /cart.js below rather than assumed.

     assets/c-cart-bundle.js clears this same key when Remove bundle wins. */
  var STORAGE_KEY = 'c-byb-state';
  var STORAGE_TTL = 7 * 24 * 60 * 60 * 1000;
  var EDIT_PARAM = 'byb_edit';
  var BUNDLE_PROP = '_byb_bundle';

  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var state = JSON.parse(raw);
      if (!state || !state.items || !state.items.length) return null;
      // A week-old selection is priced off a week-old page; start clean instead.
      if (!state.savedAt || Date.now() - state.savedAt > STORAGE_TTL) return null;
      return state;
    } catch (error) {
      return null;
    }
  }

  function writeStored(state) {
    try {
      if (state) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* private mode, or a full quota: the builder still works for this page */
    }
  }

  function editParam() {
    try {
      return new URLSearchParams(window.location.search).get(EDIT_PARAM) || '';
    } catch (error) {
      return '';
    }
  }

  var CLOSE_ICON =
    '<svg viewBox="0 0 12 12" fill="none" aria-hidden="true">' +
    '<path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

  function BuildYourBox(root) {
    this.root = root;
    this.config = parseJSONScript(root.querySelector('[data-byb-config]')) || {};
    this.labels = this.config.labels || {};
    this.tiers = (this.config.tiers || []).slice().sort(function (a, b) {
      return a.quantity - b.quantity;
    });

    this.items = [];
    // Set once these items are a line in the cart: the CTA then updates that
    // bundle instead of stacking a second copy of it next to it.
    this.cartBundleId = null;
    // userTarget is the tier the shopper picked; targetQty is what the box
    // actually shows, derived from it and from how many items are in already.
    this.userTarget = this.tiers.length ? this.tiers[0].quantity : 1;
    this.targetQty = this.userTarget;

    // A merchant can set the cap lower than the biggest tier; the tier wins.
    var biggestTier = this.tiers.length ? this.tiers[this.tiers.length - 1].quantity : 0;
    this.maxItems = Math.max(this.config.maxItems || 0, biggestTier);
    this.pdpCache = {};
    this.pdpRequest = 0;

    this.els = {
      tabs: root.querySelectorAll('[data-byb-tab]'),
      panels: root.querySelectorAll('[data-byb-panel]'),
      tierButtons: root.querySelectorAll('[data-byb-tier]'),
      slots: root.querySelector('[data-byb-slots]'),
      cta: root.querySelector('[data-byb-cta]'),
      ctaText: root.querySelector('[data-byb-cta-text]'),
      ctaPrice: root.querySelector('[data-byb-cta-price]'),
      ctaCompare: root.querySelector('[data-byb-cta-compare]'),
      footnote: root.querySelector('[data-byb-footnote]'),
      error: root.querySelector('[data-byb-error]'),
      summary: root.querySelector('[data-byb-summary]'),
      toggle: root.querySelector('[data-byb-toggle]'),
      toggleText: root.querySelector('[data-byb-toggle-text]'),
      modal: root.querySelector('[data-byb-modal]'),
      modalBody: root.querySelector('[data-byb-modal-body]')
    };

    // Mobile starts closed: the bar is the button and its directive, and the
    // products underneath get the rest of the screen. Desktop ignores this —
    // the CSS only reads the class below 990px.
    this.barCollapsed = true;
    this.upsellShownAt = null;

    this.bind();
    this.setBarCollapsed(true);
    this.render();
    this.watchSummaryBleed();
    this.watchStickyOffset();
    this.restoreState();
  }

  /* ------------------------------------------------------------ state ---- */

  BuildYourBox.prototype.cartJsonUrl = function (path) {
    var base = this.config.cartUrl || '/cart';
    return base + (path || '.js');
  };

  BuildYourBox.prototype.saveState = function () {
    if (!this.items.length) {
      writeStored(null);
      return;
    }
    writeStored({
      savedAt: Date.now(),
      bundleId: this.cartBundleId,
      userTarget: this.userTarget,
      items: this.items
    });
  };

  BuildYourBox.prototype.applyState = function (state) {
    this.items = (state.items || []).slice();
    this.cartBundleId = state.bundleId || null;
    if (state.userTarget) this.userTarget = state.userTarget;
    this.recomputeTarget();
    this.render();
  };

  /* The bundle is gone from the cart, so the builder holding on to it would
     offer an Update that has nothing left to update. */
  BuildYourBox.prototype.clearBundle = function () {
    this.items = [];
    this.cartBundleId = null;
    this.userTarget = this.tiers.length ? this.tiers[0].quantity : 1;
    this.recomputeTarget();
    this.saveState();
    this.render();
  };

  /* Every line the cart holds for one bundle id, in cart order. */
  BuildYourBox.prototype.fetchBundleLines = function (bundleId) {
    return fetch(this.cartJsonUrl(), { credentials: 'same-origin' }).
      then(function (response) {
        return response.json();
      }).
      then(function (cart) {
        return (cart.items || []).filter(function (line) {
          return line.properties && line.properties[BUNDLE_PROP] === bundleId;
        });
      }).
      catch(function () {
        return null;
      });
  };

  /* Rebuild the box from the cart itself, for the shopper who followed Edit on
     another device or after clearing their storage. compare_at_price is not in
     /cart.js, so a restored piece simply shows no strike-through. */
  BuildYourBox.prototype.itemsFromLines = function (lines) {
    var items = [];
    lines.forEach(function (line) {
      for (var i = 0; i < line.quantity; i++) {
        items.push({
          key: 'byb-' + line.key + '-' + i,
          variantId: line.variant_id || line.id,
          productId: line.product_id,
          productTitle: line.product_title || line.title,
          shortTitle: (line.properties && line.properties._byb_short) || '',
          variantTitle: line.variant_title && line.variant_title.indexOf('Default') === -1 ?
            line.variant_title :
            '',
          image: line.image || '',
          price: line.original_price != null ? line.original_price : line.price,
          comparePrice: 0,
          url: (line.url || '').split('?')[0]
        });
      }
    });
    return items;
  };

  BuildYourBox.prototype.stripEditParam = function () {
    if (!window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete(EDIT_PARAM);
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    } catch (error) {
      /* nothing to strip is not a failure */
    }
  };

  /* Three ways in, in order of how much they know:
       1. ?byb_edit=<id> — the cart's Edit link, authoritative about WHICH
          bundle is being edited;
       2. what this browser stored, which still has the compare-at prices;
       3. the cart lines themselves.
     Anything claiming to be in the cart is checked against the cart. */
  BuildYourBox.prototype.restoreState = function () {
    var self = this;
    var editId = editParam();
    var stored = readStored();

    if (!editId) {
      if (!stored) return;
      this.applyState(stored);
      // An in-progress selection was never in the cart; nothing to verify.
      if (!stored.bundleId) return;
      this.fetchBundleLines(stored.bundleId).then(function (lines) {
        // null is a failed read, not an empty cart: only an answer counts.
        if (!lines || lines.length) return;
        self.clearBundle();
      });
      return;
    }

    if (stored) this.applyState(stored);
    this.stripEditParam();

    this.fetchBundleLines(editId).then(function (lines) {
      if (!lines) return;

      // The link outlived its bundle — removed from the cart, or checked out.
      if (!lines.length) {
        if (self.cartBundleId === editId) self.clearBundle();
        return;
      }

      // Storage already holds this bundle, with the compare-at prices the cart
      // does not carry; leave it be.
      if (self.cartBundleId === editId) return;

      self.items = self.itemsFromLines(lines);
      self.cartBundleId = editId;
      self.userTarget = self.items.length;
      self.recomputeTarget();
      self.saveState();
      self.render();
    });
  };

  /* ------------------------------------------------------------ tiers ---- */

  BuildYourBox.prototype.tierForQuantity = function (quantity) {
    var match = null;
    this.tiers.forEach(function (tier) {
      if (quantity >= tier.quantity) match = tier;
    });
    return match;
  };

  BuildYourBox.prototype.nextTierAfter = function (quantity) {
    for (var i = 0; i < this.tiers.length; i++) {
      if (this.tiers[i].quantity > quantity) return this.tiers[i];
    }
    return null;
  };

  /* The box always shows the smallest tier that can still hold everything
     picked so far, never dropping below the tier the shopper chose. Removing
     an item therefore steps the goal back down instead of stranding them one
     short of a tier they already passed. */
  BuildYourBox.prototype.recomputeTarget = function () {
    var base = Math.max(this.items.length, this.userTarget);
    var tier = this.tiers.filter(function (candidate) {
      return candidate.quantity >= base;
    })[0];
    this.targetQty = tier ? tier.quantity : Math.max(base, 1);
  };

  /* Discount previewed on the cards: what the shopper gets if they fill the
     bundle they are currently aiming at. */
  BuildYourBox.prototype.targetDiscount = function () {
    var tier = this.tierForQuantity(this.targetQty);
    return tier ? tier.discount : 0;
  };

  /* Discount actually earned right now, from the items already picked. */
  BuildYourBox.prototype.earnedDiscount = function () {
    var tier = this.tierForQuantity(this.items.length);
    return tier ? tier.discount : 0;
  };

  BuildYourBox.prototype.discountedPrice = function (cents, discount) {
    if (!discount) return cents;
    return Math.round(cents * (100 - discount) / 100);
  };

  /* ------------------------------------------------------------- bind ---- */

  BuildYourBox.prototype.bind = function () {
    var self = this;

    this.els.tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        self.activateTab(tab.getAttribute('data-byb-tab'));
      });
    });

    this.els.tierButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        var quantity = parseInt(button.getAttribute('data-byb-qty'), 10);
        if (!quantity || quantity < self.items.length) return;
        self.userTarget = quantity;
        self.recomputeTarget();
        self.saveState();
        self.render();
      });
    });

    // Cards are delegated: "Select" opens the drawer, a swatch opens it with
    // that colour preselected, "Added" takes the product back out.
    this.root.addEventListener('click', function (event) {
      var swatch = event.target.closest('[data-byb-card-swatch]');
      if (swatch && self.root.contains(swatch)) {
        event.preventDefault();
        self.openProduct(swatch.closest('[data-byb-card]'), swatch.getAttribute('data-variant-id'));
        return;
      }

      var trigger = event.target.closest('[data-byb-card-open]');
      if (trigger && self.root.contains(trigger)) {
        event.preventDefault();
        var card = trigger.closest('[data-byb-card]');
        var cardVariantId = card.getAttribute('data-variant-id');

        // Clicking an already-added card adds another of the same colourway —
        // four of one colour is a real order (bridesmaid gifts). Removing is the
        // slot's own X, so the card never has to double as a toggle.
        if (self.isFull()) {
          self.setError(self.labels.bundle_full);
          return;
        }

        // A variant card opens the same drawer, just already on its own variant.
        self.openProduct(card, cardVariantId);
        return;
      }

      var remove = event.target.closest('[data-byb-remove]');
      if (remove && self.root.contains(remove)) {
        event.preventDefault();
        self.removeItem(remove.getAttribute('data-byb-remove'));
      }
    });

    if (this.els.toggle) {
      this.els.toggle.addEventListener('click', function () {
        self.setBarCollapsed(!self.barCollapsed);
      });
    }

    if (this.els.cta) {
      this.els.cta.addEventListener('click', function () {
        self.addBundleToCart();
      });
    }

    if (this.els.modal) {
      this.els.modal.addEventListener('click', function (event) {
        if (event.target.closest('[data-byb-modal-close]')) {
          event.preventDefault();
          self.closeModal();
        }
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') self.closeModal();
    });

    /* Remove bundle in the cart (assets/c-cart-bundle.js) is the only thing
       that empties the box — including while the shopper is looking at it. */
    document.addEventListener('byb:bundleremoved', function (event) {
      var removed = event.detail && event.detail.bundleId;
      if (!self.cartBundleId) return;
      if (removed && removed !== self.cartBundleId) return;
      self.clearBundle();
    });
  };

  BuildYourBox.prototype.activateTab = function (id) {
    this.els.tabs.forEach(function (tab) {
      tab.classList.toggle('is-active', tab.getAttribute('data-byb-tab') === id);
      tab.setAttribute('aria-selected', tab.getAttribute('data-byb-tab') === id ? 'true' : 'false');
    });
    this.els.panels.forEach(function (panel) {
      panel.classList.toggle('is-active', panel.getAttribute('data-byb-panel') === id);
    });
  };

  /* ------------------------------------------------------------ items ---- */

  BuildYourBox.prototype.isFull = function () {
    return !!this.maxItems && this.items.length >= this.maxItems;
  };

  BuildYourBox.prototype.addItem = function (item) {
    if (this.isFull()) return false;
    item.key = 'byb-' + Date.now() + '-' + Math.round(Math.random() * 1e6);
    this.items.push(item);
    this.recomputeTarget();
    this.saveState();
    this.render();
    this.revealUpsell();
    return true;
  };

  BuildYourBox.prototype.removeItem = function (key) {
    this.items = this.items.filter(function (item) {
      return item.key !== key;
    });
    // Taking one back out puts the threshold ahead of them again, so the tile is
    // worth showing a second time when they refill it.
    this.upsellShownAt = null;
    this.recomputeTarget();
    this.saveState();
    this.render();
  };

  /* ------------------------------------------------------- mobile bar ---- */

  BuildYourBox.prototype.setBarCollapsed = function (collapsed) {
    this.barCollapsed = !!collapsed;
    this.root.classList.toggle('is-bar-collapsed', this.barCollapsed);

    if (!this.els.toggle) return;
    this.els.toggle.setAttribute('aria-expanded', this.barCollapsed ? 'false' : 'true');
    if (this.els.toggleText) {
      this.els.toggleText.textContent = this.barCollapsed ?
        this.labels.bar_expand :
        this.labels.bar_collapse;
    }
  };

  /* Filling the tier being aimed at is the one moment the tier above it is worth
     interrupting for: open the bar and slide the slot row to its end, so "Unlock
     20% off" is what the shopper sees rather than something they have to scroll
     sideways to find. Once per threshold — reopening a bar they just closed is a
     fight they cannot win. */
  BuildYourBox.prototype.revealUpsell = function () {
    var count = this.items.length;
    if (count !== this.targetQty) return;
    if (!this.nextTierAfter(count)) return;
    if (this.upsellShownAt === count) return;

    this.upsellShownAt = count;
    this.setBarCollapsed(false);
    this.scrollSlotsToEnd();
  };

  /* Horizontal on mobile, a vertical column on desktop — where there is nothing
     to scroll this measures 0 and does nothing, which is the desktop answer. */
  BuildYourBox.prototype.scrollSlotsToEnd = function () {
    var slots = this.els.slots;
    if (!slots) return;

    // renderSlots has just replaced the row wholesale; let it lay out first.
    requestAnimationFrame(function () {
      var max = slots.scrollWidth - slots.clientWidth;
      if (max <= 0) return;
      if (slots.scrollTo) slots.scrollTo({ left: max, behavior: 'smooth' });
      else slots.scrollLeft = max;
    });
  };

  /* ----------------------------------------------------------- render ---- */

  BuildYourBox.prototype.render = function () {
    // The panel hint is the empty-box hint its own setting name says it is: once
    // a product is in there the box shows the products, and the directive under
    // the button takes over saying what to do next.
    this.root.classList.toggle('is-filled', this.items.length > 0);

    this.renderTiers();
    this.renderSlots();
    this.renderCta();
    this.renderCards();
    this.setError('');
  };

  /* The tier row is always on screen now, so the fill has to mean one thing
     only: this discount is yours. It lands on "buy 4" the moment the fourth
     item goes in and never a pick earlier, which is why it follows the tier
     EARNED rather than the tier being aimed at. `aria-pressed` still reports
     the target the shopper chose, because that is what the button toggles. */
  BuildYourBox.prototype.renderTiers = function () {
    var self = this;
    var earned = this.tierForQuantity(this.items.length);

    this.els.tierButtons.forEach(function (button) {
      var quantity = parseInt(button.getAttribute('data-byb-qty'), 10);
      button.classList.toggle('is-active', !!earned && quantity === earned.quantity);
      button.setAttribute('aria-pressed', quantity === self.targetQty ? 'true' : 'false');
    });
  };

  BuildYourBox.prototype.renderSlots = function () {
    if (!this.els.slots) return;

    var self = this;
    // The same price the grid card and the drawer quoted when this item was
    // picked. Pricing the box on the discount EARNED so far meant a product
    // read $50.15 on the left and $59.00 the moment it landed on the right,
    // which reads as the discount being taken away rather than not yet won.
    var discount = this.targetDiscount();
    var slotCount = Math.max(this.targetQty, this.items.length);
    var html = '';

    for (var i = 0; i < slotCount; i++) {
      var item = this.items[i];

      if (!item) {
        // No + sign: the slot is a placeholder for what the grid puts in it, and
        // a + on a dashed card reads as "tap here to add", which it never was.
        html +=
          '<li class="c-byb__slot c-byb__slot--empty">' +
          (self.labels.slot_empty ?
            '<span class="c-byb__slot-placeholder">' + escapeHtml(self.labels.slot_empty) + '</span>' :
            '') +
          '</li>';
        continue;
      }

      var unitPrice = self.discountedPrice(item.price, discount);
      var compare = Math.max(item.comparePrice || 0, item.price);

      html +=
        '<li class="c-byb__slot c-byb__slot--filled">' +
        '<img class="c-byb__slot-img" src="' + escapeHtml(item.image) + '" alt="" loading="lazy" width="56" height="56">' +
        '<span class="c-byb__slot-body">' +
        '<span class="c-byb__slot-title">' + escapeHtml(item.shortTitle || item.productTitle) + '</span>' +
        (item.variantTitle ?
          '<span class="c-byb__slot-variant">' + escapeHtml(item.variantTitle) + '</span>' :
          '') +
        '<span class="c-byb__slot-prices">' +
        '<span class="c-byb__slot-price">' + formatMoney(unitPrice) + '</span>' +
        (compare > unitPrice ?
          '<span class="c-byb__slot-compare">' + formatMoney(compare) + '</span>' :
          '') +
        '</span>' +
        '</span>' +
        '<button type="button" class="c-byb__slot-remove" data-byb-remove="' + escapeHtml(item.key) + '" ' +
        'aria-label="' + escapeHtml(fillTokens(self.labels.remove, { title: item.productTitle })) + '">' +
        CLOSE_ICON +
        '</button>' +
        '</li>';
    }

    // Teaser tile for the tier above the one currently being filled.
    var upsell = this.nextTierAfter(slotCount);
    if (upsell) {
      var upsellTokens = {
        discount: upsell.discount,
        qty: upsell.quantity,
        count: upsell.quantity - slotCount
      };
      // The tile is one directive now; the second line only exists if the
      // merchant fills it in. An empty span still ate the tile's own gap.
      var upsellText = fillTokens(this.labels.upsell_text, upsellTokens);

      html +=
        '<li class="c-byb__slot c-byb__slot--upsell">' +
        '<span class="c-byb__slot-upsell-title">' +
        escapeHtml(fillTokens(this.labels.upsell_title, upsellTokens)) +
        '</span>' +
        (upsellText ?
          '<span class="c-byb__slot-upsell-text">' + escapeHtml(upsellText) + '</span>' :
          '') +
        '</li>';
    }

    this.els.slots.innerHTML = html;
  };

  /* Priced off the same tier as the slots above, so the saving under the button
     is the sum of what the box is showing. Once the bundle is complete the tier
     aimed at IS the tier earned, so the total the shopper pays is unchanged. */
  BuildYourBox.prototype.totals = function () {
    var discount = this.targetDiscount();
    var subtotal = 0;
    var compare = 0;

    this.items.forEach(function (item) {
      subtotal += item.price;
      compare += Math.max(item.comparePrice || 0, item.price);
    });

    var total = this.discountedPrice(subtotal, discount);
    return { subtotal: subtotal, compare: compare, total: total, saving: Math.max(compare - total, 0) };
  };

  BuildYourBox.prototype.renderCta = function () {
    if (!this.els.cta) return;

    var count = this.items.length;
    var missing = Math.max(this.targetQty - count, 0);
    var totals = this.totals();

    if (missing > 0) {
      this.els.cta.disabled = true;
      this.els.ctaText.textContent = fillTokens(this.labels.cta_incomplete, {
        count: missing,
        qty: this.targetQty,
        discount: this.targetDiscount()
      });
      this.els.ctaPrice.textContent = '';
      this.els.ctaCompare.textContent = '';
    } else {
      this.els.cta.disabled = false;
      // Editing a bundle that is already a cart line: the same button, saying
      // what it will actually do.
      var completeLabel = this.cartBundleId ?
        this.labels.cta_update || this.labels.cta_complete :
        this.labels.cta_complete;
      this.els.ctaText.textContent = fillTokens(completeLabel, {
        count: count,
        discount: this.earnedDiscount()
      });
      this.els.ctaPrice.textContent = '• ' + formatMoney(totals.total);
      this.els.ctaCompare.textContent = totals.compare > totals.total ? formatMoney(totals.compare) : '';
    }

    if (this.els.footnote) {
      var next = this.nextTierAfter(count);
      var footnote;

      if (count === 0) {
        // Nothing picked yet, so there is no directive to give and no saving to
        // claim: the line stays empty rather than repeating the banner's
        // "up to 20% off" under the button.
        footnote = '';
      } else if (next) {
        footnote = fillTokens(this.labels.footnote_upgrade, {
          count: next.quantity - count,
          discount: next.discount,
          qty: next.quantity,
          amount: formatMoney(totals.saving)
        });
        // Only claim a saving once there actually is one.
        if (totals.saving > 0) {
          footnote += ' ' + fillTokens(this.labels.footnote_saving, {
            amount: formatMoney(totals.saving)
          });
        }
      } else {
        footnote = fillTokens(this.labels.footnote_saving, { amount: formatMoney(totals.saving) });
      }

      this.els.footnote.innerHTML = footnote;
    }
  };

  BuildYourBox.prototype.renderCards = function () {
    var self = this;
    var discount = this.targetDiscount();

    this.root.querySelectorAll('[data-byb-card]').forEach(function (card) {
      // No "added" state: the same colourway can go in several times, and a card
      // greyed out to "Added" read as disabled and stopped people picking it
      // again. What is in the bundle is the box's job to show, not the grid's.
      var soldOut = card.classList.contains('is-sold-out');
      var button = card.querySelector('[data-byb-card-toggle]');
      if (button) {
        var label = soldOut ? self.labels.card_sold_out : self.labels.card_select;
        var textEl = button.querySelector('[data-byb-card-label]');
        if (textEl) textEl.textContent = label;
      }

      // Card prices preview the tier discount before anything is picked.
      var price = parseInt(card.getAttribute('data-price'), 10) || 0;
      var comparePrice = parseInt(card.getAttribute('data-compare'), 10) || 0;
      var discounted = self.discountedPrice(price, discount);
      var strike = Math.max(comparePrice, price);

      var priceEl = card.querySelector('[data-byb-card-price]');
      var compareEl = card.querySelector('[data-byb-card-compare]');
      var btnPriceEl = card.querySelector('[data-byb-card-btn-price]');

      if (priceEl) priceEl.textContent = formatMoney(discounted);
      if (compareEl) compareEl.textContent = strike > discounted ? formatMoney(strike) : '';
      if (btnPriceEl) btnPriceEl.textContent = soldOut ? '' : formatMoney(discounted);
    });
  };

  BuildYourBox.prototype.setError = function (message) {
    if (this.els.error) this.els.error.textContent = message || '';
  };

  /* ------------------------------------------------------- mini PDP ------ */

  /* Body-scroll freeze.
     `cc-popup-no-scroll` is a single global flag that the theme's own popups
     share, so whoever removes it last wins. Track whether THIS drawer is the one
     that set it and only ever release our own lock — and never while another
     popup (the cart drawer) is still on screen. Getting this wrong leaves the
     page permanently unscrollable with no visible popup to close. */
  BuildYourBox.prototype.lockScroll = function () {
    if (this.ownsScrollLock) return;
    // Someone else already froze the page; leave their lock alone.
    if (document.body.classList.contains('cc-popup-no-scroll')) return;
    document.body.classList.add('cc-popup-no-scroll');
    this.ownsScrollLock = true;
  };

  BuildYourBox.prototype.unlockScroll = function () {
    if (!this.ownsScrollLock) return;
    this.ownsScrollLock = false;
    if (!document.querySelector('.cc-popup--visible')) {
      document.body.classList.remove('cc-popup-no-scroll');
    }
  };

  BuildYourBox.prototype.openModal = function () {
    if (!this.els.modal) return;
    this.lastFocused = document.activeElement;
    this.els.modal.classList.add('cc-popup--visible');
    this.els.modal.setAttribute('aria-hidden', 'false');
    this.lockScroll();

    var closeButton = this.els.modal.querySelector('[data-byb-modal-close]');
    if (closeButton) setTimeout(function () {closeButton.focus();}, 400);
  };

  BuildYourBox.prototype.closeModal = function () {
    if (!this.els.modal) return;

    // Always tear down, even if the visible class was already stripped by
    // something else — otherwise the lock outlives the drawer.
    this.els.modal.classList.remove('cc-popup--visible');
    this.els.modal.setAttribute('aria-hidden', 'true');
    this.unlockScroll();

    if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
    this.lastFocused = null;

    if (this.pdpSwiper) {
      this.pdpSwiper.destroy(true, true);
      this.pdpSwiper = null;
    }
  };

  BuildYourBox.prototype.openProduct = function (card, variantId) {
    if (!card) return;

    var self = this;
    var url = card.getAttribute('data-product-url');
    var handle = card.getAttribute('data-product-handle');
    if (!url) return;

    // The drawer is built from the product's own section render, which only
    // knows the full title. Carry the merchant's short name across from the card
    // so the summary can use it; only one drawer is ever open at a time.
    this.openShortName = card.getAttribute('data-short-title') || '';

    this.openModal();
    this.els.modalBody.innerHTML = '<div class="c-byb-modal__loader" role="status" aria-live="polite"></div>';

    var cacheKey = handle + '::' + (variantId || '');
    var token = ++this.pdpRequest;

    if (this.pdpCache[cacheKey]) {
      this.mountPdp(this.pdpCache[cacheKey], token);
      return;
    }

    var separator = url.indexOf('?') === -1 ? '?' : '&';
    var requestUrl = url + separator + 'section_id=' + encodeURIComponent(this.config.pdpSectionId);
    if (variantId) requestUrl += '&variant=' + encodeURIComponent(variantId);

    fetch(requestUrl).
      then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      }).
      then(function (html) {
        self.pdpCache[cacheKey] = html;
        self.mountPdp(html, token);
      }).
      catch(function () {
        if (token !== self.pdpRequest) return;
        self.els.modalBody.innerHTML =
          '<p class="c-byb__error" style="padding:40px 20px">' + escapeHtml(self.labels.pdp_error) + '</p>';
      });
  };

  BuildYourBox.prototype.mountPdp = function (html, token) {
    if (token !== this.pdpRequest) return;

    var holder = document.createElement('div');
    holder.innerHTML = html;

    var pdp = holder.querySelector('[data-byb-pdp]');
    if (!pdp) {
      this.els.modalBody.innerHTML =
        '<p class="c-byb__error" style="padding:40px 20px">' + escapeHtml(this.labels.pdp_error) + '</p>';
      return;
    }

    this.els.modalBody.innerHTML = '';
    this.els.modalBody.appendChild(pdp);
    this.initPdp(pdp);
  };

  BuildYourBox.prototype.initPdp = function (pdp) {
    var self = this;
    var data = parseJSONScript(pdp.querySelector('[data-byb-pdp-data]'));
    if (!data) return;

    var state = { options: (data.selected || []).slice() };

    var priceEl = pdp.querySelector('[data-byb-pdp-price]');
    var compareEl = pdp.querySelector('[data-byb-pdp-compare]');
    var addButton = pdp.querySelector('[data-byb-pdp-add]');
    var addPriceEl = pdp.querySelector('[data-byb-pdp-add-price]');
    var countEl = pdp.querySelector('[data-byb-pdp-count]');
    var stockEl = pdp.querySelector('[data-byb-pdp-stock]');

    function currentVariant() {
      return data.variants.filter(function (variant) {
        return variant.options.every(function (value, index) {
          return value === state.options[index];
        });
      })[0] || null;
    }

    function variantExists(optionIndex, value) {
      return data.variants.some(function (variant) {
        if (variant.options[optionIndex] !== value) return false;
        return variant.options.every(function (optionValue, index) {
          return index === optionIndex || optionValue === state.options[index];
        });
      });
    }

    function isAvailable(optionIndex, value) {
      return data.variants.some(function (variant) {
        return variant.options[optionIndex] === value && variant.available;
      });
    }

    function updateGallery(variant) {
      if (!variant || variant.media_index == null || !self.pdpSwiper) return;
      self.pdpSwiper.slideTo(variant.media_index);
    }

    function update() {
      var variant = currentVariant();
      var discount = self.targetDiscount();

      // Reflect the selection in every option row.
      pdp.querySelectorAll('[data-byb-pdp-value]').forEach(function (button) {
        var index = parseInt(button.getAttribute('data-option-index'), 10);
        var value = button.getAttribute('data-byb-pdp-value');
        button.classList.toggle('is-active', state.options[index] === value);
        button.classList.toggle('is-unavailable', !isAvailable(index, value) || !variantExists(index, value));
      });

      pdp.querySelectorAll('[data-byb-pdp-selected]').forEach(function (el) {
        var index = parseInt(el.getAttribute('data-byb-pdp-selected'), 10);
        el.textContent = state.options[index] || '';
      });

      if (variant) {
        var discounted = self.discountedPrice(variant.price, discount);
        var strike = Math.max(variant.compare_at_price || 0, variant.price);

        if (priceEl) priceEl.textContent = formatMoney(discounted);
        if (compareEl) compareEl.textContent = strike > discounted ? formatMoney(strike) : '';
        // No point advertising a price you cannot add.
        if (addPriceEl) addPriceEl.textContent = variant.available ? '• ' + formatMoney(discounted) : '';

        addButton.disabled = !variant.available;
        addButton.querySelector('[data-byb-pdp-add-label]').textContent = variant.available ?
          self.labels.pdp_add :
          self.labels.pdp_sold_out;

        if (stockEl) {
          stockEl.textContent = variant.available ? '' : self.labels.pdp_sold_out;
          stockEl.hidden = variant.available;
        }

        updateGallery(variant);
      } else {
        if (priceEl) priceEl.textContent = '';
        if (compareEl) compareEl.textContent = '';
        if (addPriceEl) addPriceEl.textContent = '';
        addButton.disabled = true;
        addButton.querySelector('[data-byb-pdp-add-label]').textContent = self.labels.pdp_unavailable;

        if (stockEl) {
          stockEl.textContent = self.labels.pdp_unavailable;
          stockEl.hidden = false;
        }
      }

      if (countEl) {
        countEl.textContent = fillTokens(self.labels.pdp_count, {
          current: Math.min(self.items.length + 1, self.targetQty),
          total: self.targetQty,
          count: Math.max(self.targetQty - self.items.length, 0)
        });
      }
    }

    pdp.addEventListener('click', function (event) {
      var button = event.target.closest('[data-byb-pdp-value]');
      if (!button) return;
      event.preventDefault();
      var index = parseInt(button.getAttribute('data-option-index'), 10);
      state.options[index] = button.getAttribute('data-byb-pdp-value');
      update();
    });

    addButton.addEventListener('click', function (event) {
      event.preventDefault();
      var variant = currentVariant();
      if (!variant || !variant.available) return;

      var added = self.addItem({
        variantId: variant.id,
        productId: data.id,
        productTitle: data.title,
        shortTitle: self.openShortName || '',
        variantTitle: variant.title === 'Default Title' ? '' : variant.title,
        image: variant.image || data.image,
        price: variant.price,
        comparePrice: variant.compare_at_price || 0,
        url: data.url
      });

      // A full bundle used to swallow the click and leave the drawer sitting
      // open with no explanation.
      self.closeModal();
      if (!added) self.setError(self.labels.bundle_full);
    });

    // Gallery: Swiper 9 is already loaded globally by layout/theme.liquid.
    var slider = pdp.querySelector('[data-byb-pdp-slider]');
    if (slider && window.Swiper) {
      // A product with one image per colour can carry 20+ media. Past the limit
      // a row of bullets is unreadable, so show "3 / 19" instead.
      var slideCount = slider.querySelectorAll('.swiper-slide').length;
      var dotsMax = this.config.dotsMax || 8;
      var useFraction = slideCount > dotsMax;

      this.pdpSwiper = new window.Swiper(slider, {
        slidesPerView: 1,
        // The drawer is mid-transition when this runs, so let Swiper re-measure
        // once it has settled rather than trusting the first read.
        observer: true,
        observeParents: true,
        watchOverflow: true,
        pagination: {
          el: pdp.querySelector('[data-byb-pdp-dots]'),
          type: useFraction ? 'fraction' : 'bullets',
          clickable: !useFraction
        }
      });

      var swiper = this.pdpSwiper;
      setTimeout(function () {
        if (swiper && !swiper.destroyed) swiper.update();
      }, 400);
    }

    update();
  };

  /* -------------------------------------------------------- add to cart -- */
  /* routes.root_url is "/" on the primary locale and "/fr" on the others. */
  BuildYourBox.prototype.discountUrl = function (code) {
    var root = this.config.rootUrl || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + 'discount/' + encodeURIComponent(code);
  };


  /* Editing an existing bundle replaces it rather than adding a second one:
     its lines come out, the new set goes in. Removal first, because adding an
     identical line first would let Shopify merge the two into one line whose
     key we would then zero — taking the whole bundle with it. */
  BuildYourBox.prototype.removeCartBundle = function (bundleId) {
    var self = this;
    return this.fetchBundleLines(bundleId).then(function (lines) {
      if (!lines || !lines.length) return null;
      var updates = {};
      lines.forEach(function (line) {
        updates[line.key] = 0;
      });
      return fetch(self.cartJsonUrl('/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates: updates })
      }).then(function (response) {
        if (!response.ok) throw new Error('cart update failed');
        self.cartBundleId = null;
        self.saveState();
        return response.json();
      });
    });
  };

  BuildYourBox.prototype.addBundleToCart = function () {
    if (!this.items.length || this.items.length < this.targetQty) return;

    var self = this;
    var previousBundleId = this.cartBundleId;
    var tier = this.tierForQuantity(this.items.length);
    var bundleName = fillTokens(this.labels.line_item_value, {
      qty: this.items.length,
      discount: this.earnedDiscount()
    });
    var properties = {};

    if (this.config.lineItemProperty) {
      properties[this.config.lineItemProperty] = bundleName;
    }

    // Underscored properties are hidden from the customer everywhere Shopify
    // prints line items, but stay readable in Liquid — which is how
    // snippets/c-cart-bundle.liquid gathers these pieces back into one line.
    var bundleId = 'byb-' + Date.now() + '-' + Math.round(Math.random() * 1e6);

    var payload = {
      items: this.items.map(function (item) {
        var line = { id: item.variantId, quantity: 1 };
        line.properties = {};
        Object.keys(properties).forEach(function (key) {
          line.properties[key] = properties[key];
        });
        line.properties._byb_bundle = bundleId;
        line.properties._byb_name = bundleName;
        // Where Edit in the cart has to send the shopper back to. Underscored,
        // so Shopify keeps it out of every customer-facing line item.
        line.properties._byb_edit = window.location.pathname;
        if (item.shortTitle) line.properties._byb_short = item.shortTitle;
        return line;
      })
    };

    this.els.cta.classList.add('is-loading');
    this.els.cta.disabled = true;
    this.setError('');

    // The discount cookie has to be set before checkout; hitting /discount/CODE
    // stores it without navigating away.
    var discountStep = tier && tier.discountCode ?
      fetch(self.discountUrl(tier.discountCode), {
        method: 'GET',
        credentials: 'same-origin'
      }).catch(function () {/* an invalid code must not block the add */}) :
      Promise.resolve();

    discountStep.
      then(function () {
        return previousBundleId ? self.removeCartBundle(previousBundleId) : null;
      }).
      then(function () {
        return fetch(self.config.cartAddUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
      }).
      then(function (response) {
        return response.json().then(function (body) {
          if (!response.ok) throw new Error(body.description || body.message || 'cart error');
          return body;
        });
      }).
      then(function () {
        self.els.cta.classList.remove('is-loading');
        // Hand the page over to the cart drawer with no drawer or lock of ours
        // left behind, so closing the cart always restores scrolling.
        self.closeModal();
        // The box is not emptied: this bundle now exists in the cart, and the
        // cart's Edit link comes straight back here to change it. Only Remove
        // bundle (or a checkout) empties it — see restoreState().
        self.cartBundleId = bundleId;
        self.saveState();
        self.render();

        // Same signal the theme's own product forms send: refreshes the cart
        // drawer markup and slides it open, no page reload.
        // A cart app (UpCart, Rebuy…) hooks the add request and opens its own
        // drawer. Asking the theme to open its drawer as well leaves two carts
        // stacked, and closing the visible one leaves the hidden one holding the
        // body-scroll lock — the page then never scrolls again.
        var openTheme = self.config.openThemeCartDrawer !== false && !hasCartApp();

        document.documentElement.dispatchEvent(
          new CustomEvent('theme:cartchanged', {
            bubbles: true,
            cancelable: false,
            detail: { openDrawer: openTheme }
          })
        );

        // Themes without a drawer still get the cart opened.
        if (openTheme && !document.querySelector('.cart-drawer')) {
          window.location = self.config.cartUrl;
        }
      }).
      catch(function (error) {
        self.els.cta.classList.remove('is-loading');
        self.renderCta();
        self.setError(error && error.message ? error.message : self.labels.cart_error);
      });
  };

  /* --------------------------------------------------- mobile spacing ---- */

  /* How far down the sticky category row has to start so it lands under the
     header instead of behind it.

     Two things make this a moving number rather than a constant. The theme
     publishes --theme-sticky-header-height, but that measures the nav bar alone
     (103px here) while what is really pinned to the top of the screen is the
     whole header section (146px), promo strip included — trusting the variable
     hid the top 43px of the tab row. And the strip retracts on the way down, so
     the header is 146px tall at rest and 103px once you are scrolling: a single
     measurement is wrong within one flick of the wheel either way.

     So follow the header's bottom edge, which is the offset by definition while
     it is pinned and goes negative — pushing nothing — once it has scrolled off
     or was never sticky to begin with. Reads are one per animation frame and
     only write when the number actually changed. */
  BuildYourBox.prototype.watchStickyOffset = function () {
    var self = this;
    var frame = 0;
    var last = null;

    var read = function () {
      frame = 0;

      var candidates = [
        document.getElementById('shopify-section-header'),
        document.querySelector('.pageheader--sticky')
      ];
      var offset = 0;

      for (var i = 0; i < candidates.length; i++) {
        var el = candidates[i];
        if (!el) continue;
        var position = window.getComputedStyle(el).position;
        if (position !== 'sticky' && position !== 'fixed') continue;
        offset = Math.max(offset, Math.round(el.getBoundingClientRect().bottom));
      }

      if (offset === last) return;
      last = offset;
      self.root.style.setProperty('--c-byb-sticky-offset', offset + 'px');
    };

    var schedule = function () {
      if (frame) return;
      frame = window.requestAnimationFrame(read);
    };

    read();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // Web fonts and the promo strip settle after first paint.
    window.addEventListener('load', schedule);
  };

  /* Under 990px the summary is a bar stuck to the bottom of the screen, and it
     lives inside .c-byb__width so its copy stays lined up with the products.
     Its background still has to reach the screen edges, so measure the gutter
     between the two and hand it to the CSS. `100vw` would say the same thing in
     one line, but it counts the scrollbar and pushes the page sideways. */
  BuildYourBox.prototype.watchSummaryBleed = function () {
    var self = this;
    if (!this.els.summary) return;

    var apply = function () {
      // Measure from a clean slate: the bleed is what moves the edge we read.
      self.root.style.setProperty('--c-byb-bleed', '0px');
      var left = self.els.summary.getBoundingClientRect().left;
      self.root.style.setProperty('--c-byb-bleed', Math.max(0, Math.round(left)) + 'px');
    };

    apply();
    window.addEventListener('resize', apply);
    // Web fonts and product images settle after first paint and can still move
    // the gutter around on the way there.
    window.addEventListener('load', apply);
  };

  /* --------------------------------------------------------------- boot -- */

  function init(scope) {
    (scope || document).querySelectorAll('[data-byb]:not([data-byb-ready])').forEach(function (root) {
      root.setAttribute('data-byb-ready', 'true');
      new BuildYourBox(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
